import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Sparkles, Sun, Salad, ShoppingCart, RefreshCw, Trash2, Search, Bell, BellOff, Copy, Share2, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { useAuth, api } from "@/lib/auth";
import {
  DndContext, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, arrayMove, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  generateWeeklyMenu, regenerateOneDay, buildShoppingList,
  loadWeeklyMenu, saveWeeklyMenu, clearWeeklyMenu,
  loadChecked, saveChecked, DAY_LABELS,
} from "@/lib/weeklyMenu";
import { registerWeeklyMenuPush, unregisterWeeklyMenuPush } from "@/lib/adminApi";

const TYPES = [
  { id: "normal",   label: "Normal",   desc: "Mezcla balanceada con carnes, pescados, caseros y pasta.", icon: Sparkles, color: "bg-amber-deep/15 text-amber-deep" },
  { id: "light",    label: "Light",    desc: "Ensaladas, pescados, sopas y vegetariano — cero fritos.", icon: Salad,    color: "bg-[var(--moss)]/20 text-[var(--moss)]" },
  { id: "especial", label: "Especial", desc: "Guisos largos, asados y platos tradicionales para engreírse.", icon: Sun,      color: "bg-orange-400/15 text-orange-600" },
];

export default function WeeklyMenu() {
  const { member } = useAuth();
  const nav = useNavigate();
  const [allRecipes, setAllRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null);          // { type, days: [], pushEnabled }
  const [step, setStep] = useState("pick");        // "pick" | "menu" | "shopping"
  const [selectedType, setSelectedType] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pickingDayIdx, setPickingDayIdx] = useState(null);
  const [checked, setChecked] = useState({});

  // Carga inicial: recetas + menú guardado (si existe)
  useEffect(() => {
    if (!member) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api().get("/recipes");
        setAllRecipes(res.data.recipes || []);
        const saved = loadWeeklyMenu(member);
        if (saved && Array.isArray(saved.days) && saved.days.length === 7) {
          setMenu(saved);
          setStep("menu");
        }
        setChecked(loadChecked(member));
      } catch (e) {
        toast.error("No pude cargar las recetas");
      } finally { setLoading(false); }
    })();
  }, [member]);

  const doGenerate = (type) => {
    if (!allRecipes.length) return toast.error("Aún no cargan las recetas");
    const days = generateWeeklyMenu(allRecipes, type);
    if (days.length < 7) return toast.error("No hay suficientes recetas para armar la semana");
    const next = { type, days, pushEnabled: false, saved_at: new Date().toISOString() };
    setMenu(next);
    saveWeeklyMenu(member, next);
    setSelectedType(type);
    setStep("menu");
    toast.success(`Menú ${type} listo — 7 platos`);
  };

  const reorderDays = (fromIdx, toIdx) => {
    if (!menu || fromIdx === toIdx) return;
    // Solo intercambiamos la RECETA de cada slot (fecha/día_label se quedan fijos)
    const recipeFields = ["slug", "nombre", "categoria", "photo", "tiempo_preparacion"];
    const days = menu.days.map((d) => ({ ...d }));
    const recipeList = days.map((d) => Object.fromEntries(recipeFields.map((f) => [f, d[f]])));
    const reordered = arrayMove(recipeList, fromIdx, toIdx);
    for (let i = 0; i < days.length; i++) {
      for (const f of recipeFields) days[i][f] = reordered[i][f];
    }
    const next = { ...menu, days };
    setMenu(next); saveWeeklyMenu(member, next);
    if (menu.pushEnabled) triggerPushSync(next);
  };

  const changeDay = (i) => {
    if (!menu) return;
    const days = regenerateOneDay(i, menu.days, allRecipes, menu.type);
    const next = { ...menu, days };
    setMenu(next); saveWeeklyMenu(member, next);
    if (menu.pushEnabled) triggerPushSync(next);
    toast.success(`Día ${days[i].day_label} cambiado`);
  };

  const pickReplacementFromLibrary = (i, r) => {
    if (!menu) return;
    const days = [...menu.days];
    days[i] = { ...days[i], slug: r.slug, nombre: r.nombre, categoria: r.categoria, photo: r.photo || null, tiempo_preparacion: r.tiempo_preparacion || "" };
    const next = { ...menu, days };
    setMenu(next); saveWeeklyMenu(member, next);
    if (menu.pushEnabled) triggerPushSync(next);
    setPickingDayIdx(null);
    toast.success(`Cambiado por ${r.nombre}`);
  };

  const startOver = () => {
    if (!window.confirm("¿Descartar el menú actual y armar uno nuevo?")) return;
    if (menu?.pushEnabled) triggerPushDisable();
    clearWeeklyMenu(member);
    setMenu(null); setStep("pick"); setSelectedType(null);
  };

  const triggerPushSync = async (m) => {
    // Reenvía el menú actualizado al Worker para que el cron mande "hoy toca X"
    try {
      await registerWeeklyMenuPush(member, m.days, m.type);
    } catch { /* silent — no hay que interrumpir edición */ }
  };
  const triggerPushDisable = async () => {
    try { await unregisterWeeklyMenuPush(member); } catch { /* silent */ }
  };

  const togglePush = async () => {
    if (!menu) return;
    setPushBusy(true);
    try {
      if (menu.pushEnabled) {
        await unregisterWeeklyMenuPush(member);
        const next = { ...menu, pushEnabled: false };
        setMenu(next); saveWeeklyMenu(member, next);
        toast.success("Ya no te vamos a avisar cada día");
      } else {
        await registerWeeklyMenuPush(member, menu.days, menu.type);
        const next = { ...menu, pushEnabled: true };
        setMenu(next); saveWeeklyMenu(member, next);
        toast.success("Papi te va a avisar cada día qué toca cocinar 📲");
      }
    } catch (e) {
      toast.error("No se pudo: " + (e.message || e));
    } finally { setPushBusy(false); }
  };

  // ==== Render ====
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-cocoa font-serif italic">Cargando recetas…</div>;
  }

  return (
    <div className="min-h-screen paper-texture iphone-safe-top">
      <header className="sticky top-0 z-30 bg-paper/95 backdrop-blur-md border-b border-line">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link data-testid="weekly-back-btn" to="/cocina" className="p-2 rounded-full bg-cream border border-line">
            <ChevronLeft className="w-5 h-5 text-cocoa" />
          </Link>
          <div className="text-center">
            <p className="eyebrow text-[10px]">Planifica tu semana</p>
            <h1 className="font-display italic text-cocoa text-2xl leading-tight">Menú Semanal</h1>
          </div>
          {menu ? (
            <button data-testid="weekly-reset-btn" onClick={startOver} className="text-xs italic text-cocoa-soft hover:underline">
              Nuevo
            </button>
          ) : <div className="w-9 h-9" />}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 pb-24 pt-6">
        {step === "pick" && <TypePicker onPick={doGenerate} />}
        {step === "menu" && menu && (
          <MenuView
            menu={menu}
            onReorder={reorderDays}
            onChange={changeDay}
            onPickManual={setPickingDayIdx}
            onGoShopping={() => setStep("shopping")}
            onTogglePush={togglePush}
            pushBusy={pushBusy}
          />
        )}
        {step === "shopping" && menu && (
          <ShoppingView
            menu={menu}
            allRecipes={allRecipes}
            checked={checked}
            setChecked={(next) => { setChecked(next); saveChecked(member, next); }}
            onBack={() => setStep("menu")}
          />
        )}
      </main>

      {pickingDayIdx != null && menu && (
        <ReplacePickerModal
          dayIndex={pickingDayIdx}
          currentSlug={menu.days[pickingDayIdx].slug}
          usedSlugs={menu.days.map((d) => d.slug)}
          allRecipes={allRecipes}
          onPick={(r) => pickReplacementFromLibrary(pickingDayIdx, r)}
          onClose={() => setPickingDayIdx(null)}
        />
      )}
    </div>
  );
}

// ==== Componentes internos =========================================

function TypePicker({ onPick }) {
  return (
    <div>
      <p className="font-serif italic text-cocoa-soft text-lg mb-6">
        Elige qué tipo de semana quieres armar. Papi te sugiere 7 platos variados y puedes ajustarlos.
      </p>
      <div className="grid grid-cols-1 gap-4">
        {TYPES.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`weekly-type-${t.id}`}
              onClick={() => onPick(t.id)}
              className="bg-cream border border-line rounded-2xl p-6 text-left hover:border-amber-deep transition flex gap-4 items-start"
            >
              <div className={`w-14 h-14 rounded-full ${t.color} flex items-center justify-center shrink-0`}>
                <Icon className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h3 className="font-display italic text-cocoa text-2xl leading-tight mb-1">Semana {t.label}</h3>
                <p className="font-serif text-cocoa-soft text-sm italic">{t.desc}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MenuView({ menu, onReorder, onChange, onPickManual, onGoShopping, onTogglePush, pushBusy }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = menu.days.map((_, i) => `slot-${i}`);
  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from < 0 || to < 0) return;
    onReorder(from, to);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <p className="eyebrow">Semana {menu.type} · {menu.days.length} platos</p>
        <button
          data-testid="weekly-push-toggle"
          onClick={onTogglePush}
          disabled={pushBusy}
          className={`px-3 py-1.5 rounded-full text-xs font-serif border transition inline-flex items-center gap-1.5 ${menu.pushEnabled ? "bg-amber-deep text-cream border-amber-deep" : "bg-cream text-cocoa border-line hover:border-amber-deep"}`}
        >
          {menu.pushEnabled ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
          {menu.pushEnabled ? "Avisos activos" : "Activar avisos diarios"}
        </button>
      </div>
      <p className="text-[11px] italic text-cocoa-soft mb-4 flex items-center gap-1.5">
        <GripVertical className="w-3 h-3 shrink-0" /> Mantén presionado el manito de arrastre pa&apos; cambiar el orden de los días.
      </p>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <ul data-testid="weekly-days" className="space-y-3 mb-8">
            {menu.days.map((d, i) => (
              <SortableDay
                key={`slot-${i}`}
                id={`slot-${i}`}
                index={i}
                day={d}
                onChange={onChange}
                onPickManual={onPickManual}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      <div className="sticky bottom-4 flex justify-center">
        <button
          data-testid="weekly-shopping-btn"
          onClick={onGoShopping}
          className="btn-primary shadow-lg"
        >
          <ShoppingCart className="w-5 h-5" /> Ver lista de compras
        </button>
      </div>
    </>
  );
}

function SortableDay({ id, index, day, onChange, onPickManual }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 20 : "auto",
  };
  const d = day;
  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`weekly-day-${index}`}
      className={`bg-cream border border-line rounded-xl p-3 flex items-center gap-2 sm:gap-3 ${isDragging ? "shadow-xl ring-2 ring-amber-deep/40" : ""}`}
    >
      <button
        {...attributes}
        {...listeners}
        data-testid={`weekly-day-${index}-drag`}
        className="p-1.5 -ml-1 rounded-md text-cocoa-soft hover:bg-paper-warm touch-none cursor-grab active:cursor-grabbing shrink-0"
        aria-label="Arrastrar para reordenar"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="w-12 sm:w-14 text-center shrink-0">
        <p className="eyebrow text-[10px]">{d.day_label.slice(0, 3)}</p>
        <p className="font-display italic text-cocoa text-lg leading-none">{parseInt(d.date.split("-")[2], 10)}</p>
      </div>
      {d.photo ? (
        <Link to={`/receta/${d.slug}`} className="shrink-0">
          <img src={d.photo} alt={d.nombre} className="w-16 h-16 object-cover rounded-md" />
        </Link>
      ) : (
        <div className="w-16 h-16 bg-paper-warm rounded-md shrink-0" />
      )}
      <Link to={`/receta/${d.slug}`} className="flex-1 min-w-0">
        <p className="eyebrow text-[10px] line-clamp-1">{d.categoria}</p>
        <h3 className="font-display italic text-cocoa text-lg leading-tight line-clamp-2">{d.nombre}</h3>
        {d.tiempo_preparacion && <p className="text-[11px] font-serif text-cocoa-soft mt-0.5">⏱ {d.tiempo_preparacion}</p>}
      </Link>
      <div className="flex flex-col gap-1 shrink-0">
        <button
          data-testid={`weekly-day-${index}-change`}
          onClick={() => onChange(index)}
          title="Sugerir otra receta"
          className="p-1.5 rounded-md bg-paper border border-line hover:border-amber-deep transition"
        ><RefreshCw className="w-3.5 h-3.5 text-cocoa" /></button>
        <button
          data-testid={`weekly-day-${index}-pick`}
          onClick={() => onPickManual(index)}
          title="Elegir manualmente"
          className="p-1.5 rounded-md bg-paper border border-line hover:border-amber-deep transition"
        ><Search className="w-3.5 h-3.5 text-cocoa" /></button>
      </div>
    </li>
  );
}

function ShoppingView({ menu, allRecipes, checked, setChecked, onBack }) {
  const embedded = useMemo(() => {
    const bySlug = new Map(allRecipes.map((r) => [r.slug, r]));
    return menu.days.map((d) => bySlug.get(d.slug)).filter(Boolean);
  }, [menu, allRecipes]);
  const shopping = useMemo(() => buildShoppingList(embedded), [embedded]);

  const toggle = (key) => {
    const next = { ...checked, [key]: !checked[key] };
    if (!next[key]) delete next[key];
    setChecked(next);
  };

  const clearChecks = () => {
    if (!Object.keys(checked).length) return;
    if (!window.confirm("¿Desmarcar todo?")) return;
    setChecked({});
  };

  const totalItems = shopping.sections.reduce((n, s) => n + s.items.length, 0);
  const checkedCount = Object.keys(checked).length;

  const asText = () => {
    let out = `🛒 Compras — Semana ${menu.type}\n\n`;
    for (const sec of shopping.sections) {
      out += `▸ ${sec.name}\n`;
      for (const it of sec.items) {
        if (checked[it.key]) continue;
        out += `  • ${it.summary}${it.count_recipes > 1 ? ` (${it.count_recipes} recetas)` : ""}\n`;
      }
      out += "\n";
    }
    return out.trim();
  };

  const copyList = () => {
    navigator.clipboard.writeText(asText()).then(() => toast.success("Lista copiada")).catch(() => toast.error("No se pudo copiar"));
  };
  const shareList = async () => {
    const txt = asText();
    if (navigator.share) {
      try { await navigator.share({ title: "Compras semana", text: txt }); } catch { /* user cancelled */ }
    } else { copyList(); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <button data-testid="shopping-back-btn" onClick={onBack} className="text-sm italic text-amber-deep hover:underline inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Volver al menú
        </button>
        <p className="eyebrow">{checkedCount}/{totalItems} ✓</p>
      </div>

      <div className="bg-cream border border-line rounded-xl p-4 mb-4 flex flex-wrap gap-2 items-center justify-between">
        <p className="font-serif italic text-cocoa text-sm">
          Marca lo que ya tienes en casa. Se guarda automático 💛
        </p>
        <div className="flex gap-1">
          <button data-testid="shopping-copy" onClick={copyList} className="p-2 rounded-full bg-paper border border-line hover:border-amber-deep transition" title="Copiar"><Copy className="w-4 h-4 text-cocoa" /></button>
          <button data-testid="shopping-share" onClick={shareList} className="p-2 rounded-full bg-paper border border-line hover:border-amber-deep transition" title="Compartir"><Share2 className="w-4 h-4 text-cocoa" /></button>
          {checkedCount > 0 && (
            <button data-testid="shopping-clear" onClick={clearChecks} className="p-2 rounded-full bg-paper border border-line hover:border-amber-deep transition" title="Desmarcar todo"><Trash2 className="w-4 h-4 text-cocoa" /></button>
          )}
        </div>
      </div>

      {shopping.sections.map((sec) => (
        <section key={sec.name} className="mb-6">
          <p className="eyebrow mb-2">{sec.name} · {sec.items.length}</p>
          <ul data-testid={`shopping-section-${sec.name}`} className="space-y-1.5">
            {sec.items.map((it) => (
              <li key={it.key}>
                <button
                  data-testid={`shopping-item-${it.key}`}
                  onClick={() => toggle(it.key)}
                  className="w-full flex items-start gap-3 p-2 rounded-md hover:bg-cream/60 transition text-left"
                >
                  <span className={`w-5 h-5 rounded border-2 shrink-0 mt-0.5 flex items-center justify-center ${checked[it.key] ? "bg-amber-deep border-amber-deep" : "bg-paper border-cocoa-soft"}`}>
                    {checked[it.key] && <span className="text-cream text-xs leading-none">✓</span>}
                  </span>
                  <div className={`flex-1 min-w-0 ${checked[it.key] ? "line-through text-cocoa-soft/60" : "text-cocoa"}`}>
                    <p className="font-serif text-sm first-letter:uppercase">{it.summary}</p>
                    {it.count_recipes > 1 && (
                      <p className="text-[10px] italic text-cocoa-soft mt-0.5">Se repite en {it.count_recipes} recetas</p>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {shopping.sections.length === 0 && (
        <p className="text-center italic text-cocoa-soft py-10">
          No hay ingredientes que listar todavía.
        </p>
      )}
    </>
  );
}

function ReplacePickerModal({ dayIndex, currentSlug, usedSlugs, allRecipes, onPick, onClose }) {
  const [q, setQ] = useState("");
  const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const filtered = useMemo(() => {
    const nq = norm(q);
    return allRecipes
      .filter((r) => r.slug !== currentSlug && (!nq || norm(r.nombre).includes(nq) || norm(r.categoria).includes(nq)))
      .slice(0, 60);
  }, [q, allRecipes, currentSlug]);

  return (
    <div className="fixed inset-0 z-50 bg-cocoa/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-cream rounded-t-2xl sm:rounded-2xl border border-line w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-line flex items-center justify-between">
          <h3 className="font-display italic text-cocoa text-xl">Elegir receta para el {DAY_LABELS[allRecipes.length ? dayIndex : 0]}</h3>
          <button data-testid="replace-modal-close" onClick={onClose} className="p-2 rounded-full bg-paper border border-line"><X className="w-4 h-4 text-cocoa" /></button>
        </div>
        <div className="p-4 border-b border-line">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cocoa-soft" />
            <input
              data-testid="replace-modal-search"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar receta…"
              className="w-full pl-10 pr-4 py-2.5 bg-paper border border-line rounded-full font-serif text-cocoa"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-2">
            {filtered.map((r) => {
              const used = usedSlugs.includes(r.slug);
              return (
                <li key={r.slug}>
                  <button
                    data-testid={`replace-pick-${r.slug}`}
                    onClick={() => onPick(r)}
                    className={`w-full text-left p-3 rounded border transition flex items-center gap-3 ${used ? "border-line bg-paper-warm opacity-50" : "border-line bg-paper hover:border-amber-deep"}`}
                    disabled={used}
                  >
                    {r.photo && <img src={r.photo} className="w-12 h-12 object-cover rounded shrink-0" alt="" />}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-cocoa font-semibold line-clamp-1">{r.nombre}</h4>
                      <p className="text-xs text-cocoa-soft line-clamp-1">{r.categoria}</p>
                    </div>
                    {used && <span className="text-xs italic text-amber-deep">Ya en la semana</span>}
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-center italic text-cocoa-soft py-10">Nada coincide</p>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
